////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// tom-select-init.ts — TOM SELECT MACHINERY
// ----------------------------------------------------------------------------------------------------
// TS port of includes/js/tom-select-init.js (Wave 2a of plans/bundler-script-load-order-phase2.md),
// itself extracted verbatim from includes/js/template.js (originally lines ~17568-18693).
//
// Loaded AFTER template.js so BASE_URL / ftDebugLog / ftResolveDynamicSelectFilterValue
// etc. exist when the functions here RUN. Module initialization creates only the local list store
// and the bottom window attaches; it performs no requests or DOM initialization. The pointer-capture install
// (fateTideInstallTomSelectPointerCapture) and the only-owned-checkbox DOMContentLoaded poll live INSIDE
// function bodies (initializeTomSelect / ftWireDynamicSelectOnlyOwnedCheckbox), so they run at
// form-interaction time, never at module load → clean 2a fold. Every external dep (BASE_URL / ftDebugLog /
// ftResolveDynamicSelectFilterValue / iconUrl / ftRefreshDynamicFilteredSelects /
// openItemInfoValue / removeSelectUserChoices / calcEssences / getSelectFeat / TomSelect /
// window.ftUpdateInventoryDescriptionScriptPreview) resolves as a window global / shared global lexical by
// call time.
//
// PUBLIC GLOBALS (window-attached at the bottom — exact public surface of the original plain-JS file):
//   destroyTomSelectIn, fateTideDecodeTomSelectLabel, ftTomSelectTruth,
//   ftTomSelectSyncSourceGatingFromNativeSelectHtml, ftWireDynamicSelectOnlyOwnedCheckbox,
//   fateTideInstallTomSelectPointerCapture, ftResolveSelectIconKeys, ftAjaxOptionIcon,
//   ftClearDynamicSelectOptionsCache (shared dynamic-select option-list cache purge),
//   initializeTomSelect, syncUserChoiceTomSelectsFromNative, ensureDynamicTomSelectOption, setTomSelectValue
//
// Cross-file consumers (grep-confirmed) include: template.js, character-creator, status-effect,
// class-summary, player-character-sheet, list-display, object-card, inventory-edit-form, inventory-actions,
// encounter-ambience, status-effect-actions, locations, side-panels-init.js, and many PHP/JS files
// (inventory_layer_rows.js, creature_model_autofill.js, create_monster_template.js, ft3d_scene_host.php,
// modify_campaign.php, edit_random_table.php, base_default_location_form_inc.php, …). All call these via
// bare-identifier / window.* globals → the window attaches below preserve them.
//
// Pivoted from bootstrap-select to Tom Select; this file holds the custom code layered on
// top to address Tom Select's defaults around source gating, label decoding, dynamic AJAX
// loading, side-info panel z-index, leaflet popup propagation, character-creator quirks,
// etc. Treat as battle-tested — refactor only with deliberate scope, smoke testing.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// TOM SELECT: tear down before replacing container HTML (avoids orphaned dropdown/search UI on body)
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { sharedDynamicSelectOptionLists } from './option-lists';
import { installBodyDropdownPlacement } from './body-dropdown-placement';
import { hydrateDynamicOptionPictures } from './option-pictures';
import { creatorAncestryPreviewKit, creatorUnifiedPreviewEnabled } from '../shared/creator-ancestry-preview';
import { abilityTypeForKey } from '../shared/icon-type-badges';

function destroyTomSelectIn(root: any): void {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll('select').forEach(function (sel: any) {
        if (sel.tomselect) {
            try {
                sel.tomselect.destroy();
            } catch (e) {
                ftDebugLog('destroyTomSelectIn', { err: String(e) });
            }
        }
    });
}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// TOM SELECT: normalize labels that contain literal HTML entities (e.g. Devil&#039;s / Devil&amp;#039;s → Devil's)
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function fateTideDecodeTomSelectLabel(s: any): string {
    if (s == null || typeof s !== 'string') {
        return '';
    }
    let t = s.trim();
    if (!t) {
        return t;
    }
    for (let i = 0; i < 6; i++) {
        const el = document.createElement('textarea');
        el.innerHTML = t;
        const next = (el.value || '').trim();
        if (next === t) {
            break;
        }
        t = next;
    }
    return t;
}

/**
 * Tom Select option objects often carry `gated` / `grandfathered` from HTML data-* attrs as strings.
 * In JS, any non-empty string (including "0") is truthy — use this for consistent booleans.
 */
function ftTomSelectTruth(v: any): boolean {
    if (v === true || v === 1) {
        return true;
    }
    if (v === false || v === 0 || v === null || v === undefined) {
        return false;
    }
    if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        return s === '1' || s === 'true' || s === 'yes';
    }
    return false;
}

/**
 * Tom Select does not copy arbitrary data-* from native <option> into ts.options.
 * Source gating needs gate_url / gated / grandfathered on every row for onItemAdd + render.
 */
function ftTomSelectSyncSourceGatingFromNativeSelectHtml(sel: any, ts: any): void {
    if (!sel || !ts || typeof ts.options !== 'object') {
        return;
    }
    const resolveOpt = function (value: any) {
        if (!value) {
            return null;
        }
        if (ts.options[value]) {
            return ts.options[value];
        }
        const lower = value.toLowerCase();
        const key = Object.keys(ts.options).find(function (k: any) {
            return k.toLowerCase() === lower;
        });
        return key ? ts.options[key] : null;
    };
    let touched = false;
    Array.from(sel.querySelectorAll('option')).forEach(function (opt: any) {
        const v = opt.value;
        if (!v) {
            return;
        }
        const ex = resolveOpt(v);
        if (!ex) {
            return;
        }
        if (opt.dataset.gated === '1') {
            ex.gated = true;
            touched = true;
        } else if (opt.dataset.gated === '0') {
            ex.gated = false;
            touched = true;
        }
        if (opt.dataset.grandfathered === '1') {
            ex.grandfathered = true;
            touched = true;
        } else if (opt.dataset.grandfathered === '0') {
            ex.grandfathered = false;
            touched = true;
        }
        const gu = (opt.dataset.gateUrl || opt.getAttribute('data-gate-url') || '').trim();
        if (gu) {
            if (ex.gate_url !== gu) {
                ex.gate_url = gu;
                touched = true;
            }
        } else if (opt.dataset.gated === '0' && ex.gate_url) {
            delete ex.gate_url;
            touched = true;
        }
    });
    if (touched) {
        if (typeof ts.refreshOptions === 'function') {
            ts.refreshOptions(false);
        }
        if (typeof ts.refreshItems === 'function') {
            ts.refreshItems();
        }
    }
}

/** Optional "hide locked" for source-gated Tom Selects (pairs with data-ft-only-owned-checkbox). */
function ftWireDynamicSelectOnlyOwnedCheckbox(sel: any, ts: any): void {
    const cbId = sel.dataset.ftOnlyOwnedCheckbox;
    if (!cbId || !ts) {
        return;
    }

    const wire = () => {
        const cb = document.getElementById(cbId) as any;
        if (!cb || cb.tagName !== 'INPUT') {
            return false;
        }
        if (cb.dataset.ftOnlyOwnedWired === '1') {
            sel._ftOnlyOwnedSearch = cb.checked;
            return true;
        }
        cb.dataset.ftOnlyOwnedWired = '1';
        const lsKey = sel.dataset.ftOnlyOwnedLsKey || 'ft_hide_locked_options';
        let v = localStorage.getItem(lsKey);
        if (v !== '1' && v !== '0') {
            v = localStorage.getItem('ft_dynamic_select_only_owned');
        }
        if (v !== '1' && v !== '0') {
            v = sel.dataset.ftDefaultOnlyOwned === 'true' ? '1' : '0';
            localStorage.setItem(lsKey, v);
        }
        cb.checked = v === '1';
        sel._ftOnlyOwnedSearch = cb.checked;
        cb.addEventListener('change', function () {
            const on = cb.checked ? '1' : '0';
            localStorage.setItem(lsKey, on);
            sel._ftOnlyOwnedSearch = cb.checked;
            if (typeof ts.clearOptions === 'function') {
                ts.clearOptions();
            }
            if (typeof ts.load === 'function') {
                const q =
                    typeof ts.control_input !== 'undefined' && ts.control_input && ts.control_input.value
                        ? ts.control_input.value
                        : typeof ts.lastQuery === 'string'
                          ? ts.lastQuery
                          : '';
                ts.load(q);
            }
        });
        return true;
    };

    if (wire()) {
        return;
    }

    let attempts = 0;
    const maxAttempts = 80;
    const poll = () => {
        if (wire()) {
            return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
            return;
        }
        setTimeout(poll, 50);
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', poll, { once: true });
    } else {
        poll();
    }
}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// TOM SELECT: remember last pointerdown target so single-select can blur after option click (not keyboard Enter)
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function fateTideInstallTomSelectPointerCapture(): void {
    if (typeof window === 'undefined' || window._ftTomSelectPointerCaptureInstalled) {
        return;
    }
    window._ftTomSelectPointerCaptureInstalled = true;
    document.addEventListener('pointerdown', function (e: any) {
        window._ftTomSelectLastPointerDown = e.target;
    }, true);
}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// TOM SELECT INITIALIZATION (pure alphabetical sorting including selected items + respects custom backend order types)
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Phase 3 (plans/icon-map-from-php-is-misty-otter.md): resolve <option data-icon-key="…" [data-icon-type]>
// placeholders into data-icon HTML BEFORE Tom Select reads them. Static and AJAX options share the
// same registry/type-badge resolver. Explicit data-icon HTML is left alone.
function ftResolveSelectIconKeys(scope: any): void {
    if (typeof iconUrl !== 'function') return;
    (scope || document).querySelectorAll('option[data-icon-key]').forEach(function (opt: any) {
        if (opt.dataset.icon) return; // explicit icon wins
        opt.dataset.icon = ftAjaxOptionIcon({ icon_key: opt.dataset.iconKey, item_type: opt.dataset.iconType });
    });
}

// Preserve bespoke pictures and catalog art; type chips use the shared rune renderer, including
// old responses that pre-resolved a badge-only key into a nonexistent PNG URL.
function ftAjaxOptionIcon(o: any): string {
    const badge = abilityTypeForKey(o.icon_key);
    if (badge) return iconMap(badge, 25, 25, false);
    let src = o.icon_url || '';
    const pngKey = typeof src === 'string' && src.match(/^https:\/\/cdn\.fatetide\.com\/images\/icons\/([^/?]+)\.png(?:[?#].*)?$/)?.[1];
    const pngBadge = abilityTypeForKey(pngKey);
    if (pngBadge) return iconMap(pngBadge, 25, 25, false);
    if (!src && typeof iconUrl === 'function') {
        src = o.icon_key ? iconUrl(o.icon_key) : '';
        if ((!src || src.indexOf('help-circle-outline') !== -1) && o.item_type) {
            if (abilityTypeForKey(o.item_type)) return iconMap(o.item_type, 25, 25, false);
            src = iconUrl(o.item_type);
        }
    }
    return src ? ('<img src="' + src + '" width="25" height="25" alt="Icon" onerror="this.src=\'https://cdn.fatetide.com/images/icons/help-circle-outline.png\'; this.onerror=null;">') : '';
}

// A static <option data-icon> ships an INERT icon_ph() placeholder ("<i class='ft-icon' data-icon='key'>"),
// not a resolved <img>. Tom Select's render output is injected into a lazily-built, body-mounted dropdown
// (and the control) that the global ftMountIcons() page sweep never reaches — so those placeholders stay
// blank (regression from the icon-placeholder migration; dynamic selects are unaffected because their
// load() builds a real <img> via ftAjaxOptionIcon). Resolve the placeholder to final icon HTML here,
// reusing ftMountIcons() itself on a DETACHED node (zero logic duplication: handles iconMap keys, data-pic
// catalog art, ability-type rune badges, fallback). No-op for already-resolved icons — an <img> or the
// hydrated "ft-icon-map" span carries no "<i " tag, so the guard returns the input untouched. Idempotent
// and cheap; render runs on every option refresh, so this stays correct across search/filter re-renders.
function ftHydrateOptionIconHtml(raw: any): string {
    if (typeof raw !== 'string' || raw === '') return '';
    if (raw.indexOf('ft-icon') === -1 || !/<i[\s>]/.test(raw)) return raw; // not an icon_ph placeholder
    const mount = (typeof window !== 'undefined') ? (window as any).ftMountIcons : null;
    if (typeof mount !== 'function') return raw;
    try {
        const tmp = document.createElement('div');
        tmp.innerHTML = raw;
        mount(tmp);
        return tmp.innerHTML;
    } catch (e) {
        return raw;
    }
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// TOM SELECT: shared option-list cache for dynamic selects
// ----------------------------------------------------------------------------------------------------
// get_items_for_dynamic_select.php ignores `term` and returns the FULL list for a type-key, yet every
// Tom Select instance refetched it on first focus (the loadingPromises dedupe only covers in-flight
// requests). On pages with many per-row dynamic selects (e.g. edit_random_table.php's item_id_attached_N,
// type all_items) that meant one heavyweight fetch PER ROW. Cache the RAW server list per type-key so the
// first load pays the fetch and every later instance/keystroke resolves instantly.
//
// The cached list must be shareable across instances, but the server response is per-request in exactly
// one way: ids sent as `preselected` get the grandfather decor (gated → selectable) baked in by
// ft_source_gating_decorate_options(). So cached fetches go out preselected-NEUTRAL and each instance
// replays that rule locally via ftApplyPreselectedGrandfather() on shallow COPIES (Tom Select mutates
// option objects it adopts — shared references would leak one instance's state into the cache).
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const ftDynamicSelectLists = sharedDynamicSelectOptionLists();

/**
 * The page's campaign, for the server's TABLE LICENSE (a campaign lends its members' PURCHASED
 * sources, so inventory/creature options the table owns render selectable instead of locked — which
 * is what the write paths already accept). Read from the boot island (window.FateTide.campaign.id),
 * which ft_boot_render() resolved server-side; '' off-campaign (catalog/creator pages) = personal only.
 *
 * This value is part of the option-list CACHE KEY below and of the request, and both must match the
 * server's $key in get_items_for_dynamic_select_inc.php byte-for-byte.
 */
function ftDynamicSelectCampaignId(): string {
    const camp = (window as any)?.FateTide?.campaign?.id;
    return typeof camp === 'string' ? camp.trim() : '';
}

/** Campaign segment of the option-list key. Empty off-campaign, so existing keys are unchanged. */
function ftDynamicSelectCampaignKeySeg(): string {
    const camp = ftDynamicSelectCampaignId();
    return camp !== '' ? '_camp' + camp : '';
}

/** Drop cached dynamic-select lists; optional prefix (e.g. 'all_items') limits the purge. Call after creating/renaming items when a fresh dropdown matters before the TTL lapses. */
function ftClearDynamicSelectOptionsCache(prefix?: string): void {
    ftDynamicSelectLists.clear(prefix);
}

function ftInventoryLayerRowFilterValue(sel: any): string {
    if (!sel || !sel.closest || sel.dataset?.ftRole !== 'inv-item-select') {
        return '';
    }
    const row = sel.closest('tr.ft-inv-layer-row');
    const filter = row ? row.querySelector('[data-ft-role="inv-item-type-filter"]') as any : null;
    if (!filter) {
        return '';
    }
    if (filter.tomselect && typeof filter.tomselect.getValue === 'function') {
        const v = filter.tomselect.getValue();
        return Array.isArray(v) ? String(v.find(Boolean) ?? '').trim() : String(v ?? '').trim();
    }
    return String(filter.value || '').trim();
}

function ftDynamicFilterValueForSelect(sel: any): string {
    return ftInventoryLayerRowFilterValue(sel) || ftResolveDynamicSelectFilterValue(sel);
}

// Per-instance replay of the server's preselected grandfathering (ft_source_gating_decorate_options):
// a gated option that is THIS instance's saved value renders selectable (grandfathered) instead of locked.
// Always returns shallow copies — callers hand these to Tom Select, which mutates what it adopts.
function ftApplyPreselectedGrandfather(rawOptions: any[], preselected: any[]): any[] {
    const pre: Record<string, boolean> = {};
    (Array.isArray(preselected) ? preselected : []).forEach(function (v: any) {
        if (v === null || v === undefined) return;
        const s = String(v).trim().toLowerCase();
        if (s !== '') {
            pre[s] = true;
        }
    });
    return (Array.isArray(rawOptions) ? rawOptions : []).map(function (o: any) {
        const copy = { ...o };
        const val = copy.value || copy.item_id || copy.id || '';
        if (copy.gated && !copy.grandfathered && val && pre[String(val).toLowerCase()]) {
            copy.gated = false;
            copy.grandfathered = true;
            copy.gate_url = null;
        }
        return copy;
    });
}

function initializeTomSelect(root: ParentNode = document): void {
    fateTideInstallTomSelectPointerCapture();
    ftResolveSelectIconKeys(root); // data-icon-key → data-icon before Tom Select reads the options
    root.querySelectorAll('select.ts-select:not(.tomselected)').forEach((sel: any) => {
        const isFeatTomSelect = sel.classList.contains('feat') && !!sel.dataset.featSourceId;
        const isMultiple = sel.multiple;
        const preloadFull = sel.dataset.preloadFull === 'true';
        const showIcons = sel.dataset.showIcons !== 'false'; // default: true (show icons)
        const type = sel.dataset.itemType;
        const homebrew = sel.dataset.homebrew === 'true';
        const thumbnailSource = (sel.dataset.thumbnailSource && ['icon', 'item_picture'].includes(sel.dataset.thumbnailSource))
            ? sel.dataset.thumbnailSource
            : 'icon';

        // NEW: Detect if this is a dynamic (server-loaded) select or static (pre-populated HTML options)
        const isDynamic = !!type;
        const sourceGating = sel.dataset.sourceGating === 'true';
        let preselectedList = [];
        try {
            preselectedList = JSON.parse(sel.dataset.preselected || '[]');
        } catch (e) {
            preselectedList = [];
        }
        if (!Array.isArray(preselectedList)) {
            preselectedList = [];
        }
        sel._ftOnlyOwnedSearch = false;

        const deferPreloadPane = sel.closest('.tab-pane[data-ft-ts-defer-preload-until-tab-visible="1"]');
        // Scene panels initialize before their final visibility is applied. Focus is the reliable
        // demand signal in both the standalone editor and the campaign's hidden editor iframe.
        const scenePicker = document.getElementById('ft_scene_builder_route_bootstrap') !== null;
        const deferTomSelectAjaxPreload = scenePicker
            || !!(deferPreloadPane && !deferPreloadPane.classList.contains('show'));

        let dynamicPreloadTomSelect: any = false;
        if (isDynamic) {
            dynamicPreloadTomSelect = preloadFull ? true : 'focus';
            if (preloadFull && deferTomSelectAjaxPreload) {
                dynamicPreloadTomSelect = 'focus';
            }
        }

        // Types that have intentional custom order from backend – preserve exact server order (no sorting)
        const customOrderTypes = ['range_type', 'damage_type', 'campaign_difficulty']; // preserve backend / get_options_for_type order
        const isCustomOrder = isDynamic && customOrderTypes.includes(type);

        // ------------------------------------------------------------------
        // CONDITIONAL PLACEHOLDER LOGIC - THIS IS THE KEY FIX FOR WIDTH
        // ------------------------------------------------------------------
        const isCompact = sel.classList.contains('ts-auto-width');
        const isFullWidth = sel.classList.contains('ts-control-full-width');
        const isIconOnlyDamage = sel.classList.contains('icon-only-damage-select');
        // Icon-only damage options author their human-readable name in title so the native
        // markup stays visually compact. Give Tom Select that name before it parses/caches the
        // options; our renderer still omits the label span, while search, hover titles and ARIA
        // labels are correct for every row (not only whichever option happened to be selected).
        if (isIconOnlyDamage) {
            Array.from(sel.options).forEach(function (opt: any) {
                if (opt.textContent.trim()) return;
                const label = String(opt.getAttribute('title') || '').trim();
                if (label) opt.textContent = label;
            });
        }
        // Compact controls stay narrow when empty and resize only to the selected option, so the
        // default is a bare '-'. An EXPLICIT placeholder attribute still wins, letting a compact
        // picker name what it picks ("Summon", "Transform") — the icon-only damage-type picker
        // passes none and keeps the '-' it has always had.
        const placeholder = isCompact
            ? (sel.getAttribute('placeholder') || '-')
            : (sel.getAttribute('placeholder') || sel.title || '-- SELECT --');

        // Detect if this select is inside a Leaflet popup (critical for the event propagation fix)
        const isInPopup = !!sel.closest('.leaflet-popup');

        // Side info panel: fixed panel + overflow clips Tom Select menus; mount dropdown on body above panel z-index
        const isInSideInfoPanel = !!sel.closest('#sideInfoPanel');

        const isInAbilityLimitations = !!sel.closest('#div_ability_limitations');

        // Character builder: long class/subclass columns + many stacked user-choice picks; in-wrapper dropdowns were
        // painted under later .ts-control siblings (two-column layout + table). Mount on body like feats/side panel.
        const isInCreatePlayerCharacterForm = !!sel.closest('#form_create_player_character');
        const creatorQuery = new URLSearchParams(window.location.search);
        const showAncestryPreviewBadge = isInCreatePlayerCharacterForm
            && ['ancestry_1', 'ancestry_2', 'ancestry_choice_1'].includes(sel.id)
            && creatorUnifiedPreviewEnabled(creatorQuery);
        const ancestryPreviewBadge = (data: any, label: string): string => {
            if (!showAncestryPreviewBadge || !creatorAncestryPreviewKit(String(data.value ?? ''), label)) return '';
            return '<img class="ft-ancestry-preview-icon" src="https://cdn.fatetide.com/images/icons/creator-3d-preview.png" width="24" height="24"'
                + ' alt="3D preview available" title="3D preview available"'
                + ' style="width:24px;height:24px;object-fit:contain;flex-shrink:0;margin-left:0.5rem;">';
        };

        const isInModifyEncounterInventoryTab = !!sel.closest('#modify_encounter_tab_pane_inventory');
        const isInModifyEncounterLairsTab = !!sel.closest('#modify_encounter_tab_pane_lairs');
        const isInEncounterInvAddRow = !!sel.closest('.ft-encounter-inv-add-row');
        const isInInventoryLayerRows = !!sel.closest('.ft-inv-layer-rows');

        // Pin dropdown width to the control (avoids fit-content menus wider than the field, e.g. class/subclass on item.php)
        const matchDropdownToControl = sel.classList.contains('ts-dropdown-match-control');

        // Dynamic multi-selects often ship with only the empty placeholder <option> until AJAX runs;
        // Tom Select's checkbox_options plugin can throw when there is only one native option.
        const nativeValuedOptions = Array.from(sel.options).filter(function (o: any) {
            return o.value !== '' && o.value != null;
        });
        const skipCheckboxOptionsPlugin = isMultiple && isDynamic && nativeValuedOptions.length <= 1;
        const tsConfig: any = {
            plugins: isMultiple
                ? (skipCheckboxOptionsPlugin ? ['remove_button'] : ['remove_button', 'checkbox_options'])
                : ['clear_button'], // single-select: '×' affordance to empty the field (pairs w/ Backspace/Delete clear below)
            preload: dynamicPreloadTomSelect,
            sortField: (isDynamic && !isCustomOrder) ? function(a: any, b: any) {
                const textA = (a.text ?? '').trim();
                const textB = (b.text ?? '').trim();
                return textA.localeCompare(textB, undefined, { sensitivity: 'base' });
            } : null,
            load: isDynamic ? function(this: any, query: any, callback: any) {
		const typeLive = (sel.dataset.itemType || '').trim();
				if (!typeLive) {
					callback([]);
					return;
				}
				const filterItemType = ftDynamicFilterValueForSelect(sel);
				const onlyOwned = !!sel._ftOnlyOwnedSearch;
				const requireExplicitSourceEntitlement = sel.dataset.requireExplicitSourceEntitlement === 'true';
				const campaignIdForSelect = ftDynamicSelectCampaignId();
				const key = `${typeLive}_${homebrew ? '1' : '0'}_${thumbnailSource}${filterItemType ? '_' + filterItemType : ''}_${onlyOwned ? 'own1' : 'own0'}_${requireExplicitSourceEntitlement ? 'claim1' : 'claim0'}${ftDynamicSelectCampaignKeySeg()}`;

				// Serve THIS instance from a shared RAW server list: per-instance stale-config guards,
                // preselected-grandfather replay, icon/label mapping, preselected-label patch, callback.
                const finishWithRawList = (rawList: any[]) => {
                    if (sel.tomselect !== this || ftDynamicSelectCampaignId() !== campaignIdForSelect
                        || (sel.dataset.itemType || '').trim() !== typeLive
                        || (sel.dataset.thumbnailSource === 'item_picture' ? 'item_picture' : 'icon') !== thumbnailSource
                        || (sel.dataset.showIcons !== 'false') !== showIcons) {
                        callback([]);
                        return;
                    }
                    if (ftDynamicFilterValueForSelect(sel) !== filterItemType) {
                        callback([]);
                        return;
                    }
                    if (!!sel._ftOnlyOwnedSearch !== onlyOwned) {
                        callback([]);
                        return;
                    }
                    if ((sel.dataset.requireExplicitSourceEntitlement === 'true') !== requireExplicitSourceEntitlement) {
                        callback([]);
                        return;
                    }

                    const options = ftApplyPreselectedGrandfather(rawList, preselectedList).map((o: any) => {
                        const gated = !!o.gated;
                        const grandfathered = !!o.grandfathered;
                        const gateUrl = o.gate_url || '';
                        let iconHtml = '';
                        if (sourceGating && gated && !grandfathered) {
                            iconHtml = '<span class="ts-gate-lock-icon text-warning" aria-hidden="true" title="Locked">&#128274;</span>';
                        } else if (showIcons && (o.icon_url || o.icon_key || o.item_type)) {
                            iconHtml = ftAjaxOptionIcon(o);
                        }
                        return {
                            value: o.value || o.item_id || o.id || '',
                            text: fateTideDecodeTomSelectLabel(o.label || o.item_name || o.name || 'Unknown'),
                            // Searchable alongside the label — see the searchField note below.
                            item_type: String(o.item_type || ''),
                            icon: iconHtml,
                            gated,
                            grandfathered,
                            gate_url: gateUrl,
                        };
                    });

                    // Server-rendered preselected options may carry a stale "Unknown (uuid...)" label
                    // when the per-item Redis cache missed at render time. Tom Select's addOption
                    // won't overwrite an existing option, so patch labels/icons here using the
                    // authoritative list before Tom Select re-renders items and options.
                    try {
                        const ts = sel.tomselect;
                        if (ts && options.length) {
                            let patched = false;
                            options.forEach(function (fresh: any) {
                                if (!fresh || !fresh.value) {
                                    return;
                                }
                                const existing = ts.options[fresh.value];
                                if (!existing) {
                                    return;
                                }
                                const existingText = typeof existing.text === 'string' ? existing.text : '';
                                const looksUnknown = /^Unknown(\s*\(|\s*$)/i.test(existingText);
                                if (fresh.text && fresh.text !== existingText && (looksUnknown || !existingText)) {
                                    existing.text = fresh.text;
                                    patched = true;
                                }
                                if (showIcons && fresh.icon && existing.icon !== fresh.icon) {
                                    existing.icon = fresh.icon;
                                    patched = true;
                                }
                                if (sourceGating) {
                                    if (fresh.gated !== undefined) {
                                        existing.gated = ftTomSelectTruth(fresh.gated);
                                    }
                                    if (fresh.grandfathered !== undefined) {
                                        existing.grandfathered = ftTomSelectTruth(fresh.grandfathered);
                                    }
                                    if (fresh.gate_url !== undefined) existing.gate_url = fresh.gate_url;
                                }
                            });
                            if (patched) {
                                if (typeof ts.refreshOptions === 'function') {
                                    ts.refreshOptions(false);
                                }
                                if (typeof ts.refreshItems === 'function') {
                                    ts.refreshItems();
                                }
                            }
                        }
                    } catch (patchErr) {
                        ftDebugLog('tomSelect', { warn: 'preselected-label patch failed', err: String(patchErr) });
                    }
                    callback(options);
                };

                const typePayload: any = {
					type: typeLive,
					homebrew,
					thumbnail_source: thumbnailSource,
					// Neutral on purpose: the response is cached across instances; per-instance
					// grandfathering is replayed in ftApplyPreselectedGrandfather (finishWithRawList).
					preselected: [],
					only_owned: onlyOwned,
					require_explicit_source_entitlement: requireExplicitSourceEntitlement,
				};
                if (filterItemType) {
                    typePayload.filter_item_type = filterItemType;
                }
                // Must be sent whenever the key includes it, or the server keys its bucket differently
                // than we look it up.
                if (campaignIdForSelect !== '') {
                    typePayload.campaign_id = campaignIdForSelect;
                }

                ftDynamicSelectLists.load(BASE_URL + 'options/tools/items/get_items_for_dynamic_select.php', key, typePayload)
                .then(rawList => thumbnailSource === 'item_picture' && showIcons
                    ? hydrateDynamicOptionPictures(rawList, BASE_URL).then(finishWithRawList)
                    : finishWithRawList(rawList))
                .catch((err: any) => {
                    console.error('Load failed for ' + key, err);
                    callback([]);
                });
            } : null,
            onChange: function(this: any) {
                if (sel.classList.contains('select_user_choice')) {
                    sel.dataset.userChoiceSelected = this.getValue() || '';
                }
                if (this.wrapper.classList.contains('feat')) {
                    this.wrapper.style.width = '100%';
                    this.control.style.width = '100%';
                } else if (isFullWidth || isInAbilityLimitations || isInEncounterInvAddRow || isInInventoryLayerRows) {
                    this.wrapper.style.width = '100%';
                    this.control.style.width = '100%';
                    if (this.dropdown) {
                        if (!matchDropdownToControl) {
                            this.dropdown.style.width = 'fit-content';
                        } else {
                            const w = Math.round(this.control.getBoundingClientRect().width);
                            if (w > 0) {
                                this.dropdown.style.setProperty('width', w + 'px', 'important');
                                this.dropdown.style.setProperty('max-width', w + 'px', 'important');
                            }
                        }
                    }
                } else {
                    this.wrapper.style.width = 'fit-content';
                    this.control.style.width = 'fit-content';
                    if (this.dropdown) {
                        if (!matchDropdownToControl) {
                            this.dropdown.style.width = 'fit-content';
                        } else {
                            const w = Math.round(this.control.getBoundingClientRect().width);
                            if (w > 0) {
                                this.dropdown.style.setProperty('width', w + 'px', 'important');
                                this.dropdown.style.setProperty('max-width', w + 'px', 'important');
                            }
                        }
                    }
                }
                const self = this;
                if (!self.isOpen) {
                    self.refreshOptions(false);
                } else {
                    queueMicrotask(function () {
                        if (!self.isOpen && !self.isDisabled) {
                            self.refreshOptions(false);
                        }
                    });
                }
                if (sel.id === 'inventory_written_in_language' && typeof window.ftUpdateInventoryDescriptionScriptPreview === 'function') {
                    const invForm = sel.closest('.inventory-container-form');
                    if (invForm) {
                        window.ftUpdateInventoryDescriptionScriptPreview(invForm);
                    }
                }
                if (sel.id) {
                    ftRefreshDynamicFilteredSelects(sel);
                }
                if (sel.dataset.formQueryItemInfo === 'true' && typeof openItemInfoValue === 'function' && sel.id) {
                    openItemInfoValue(sel.id);
                }
            },
            onItemAdd: function (this: any, value: any) {
                if (sourceGating && value) {
                    const opt = this.options[value];
                    if (opt && ftTomSelectTruth(opt.gated) && !ftTomSelectTruth(opt.grandfathered)) {
                        this.removeItem(value, true);
                        if (opt.gate_url) {
                            window.open(opt.gate_url, '_blank', 'noopener,noreferrer');
                        }
                        return;
                    }
                }
                if (isMultiple) return;
                const t = window._ftTomSelectLastPointerDown;
                if (!t || !this.dropdown || typeof t.closest !== 'function') return;
                if (!this.dropdown.contains(t)) return;
                if (!t.closest('[data-selectable]')) return;
                const self = this;
                queueMicrotask(function () {
                    if (self && typeof self.blur === 'function') {
                        self.blur();
                    }
                });
            },
            onLoad: isDynamic ? function(this: any) {
                this.refreshOptions(false);
            } : undefined,
            render: {
                option: function(data: any, escape: any) {
                    const label = fateTideDecodeTomSelectLabel(data.text || '');
                    const gatedFlag = ftTomSelectTruth(data.gated);
                    const grandfatheredFlag = ftTomSelectTruth(data.grandfathered);
                    const isGatedRow = sourceGating && gatedFlag && !grandfatheredFlag;
                    const isGrandfathered = sourceGating && grandfatheredFlag;
                    let icon = '';
                    if (isGatedRow) {
                        icon = '<span class="ts-gate-lock-icon text-warning" aria-hidden="true" title="Locked">&#128274;</span>';
                    } else if (showIcons) {
                        icon = ftHydrateOptionIconHtml(data.icon || '');
                    }
                    const rowClasses = ['d-flex', 'align-items-center', 'justify-content-start', 'w-100'];
                    if (isGatedRow) rowClasses.push('ts-option-gated');
                    if (isGrandfathered) rowClasses.push('ts-option-grandfathered');
                    const labelSpan = isIconOnlyDamage ? '' : (matchDropdownToControl
                        ? '<span class="ts-match-control-label" style="margin-left:0.75rem;">' + escape(label) + '</span>'
                        : '<span class="ms-2">' + escape(label) + '</span>');
                    let extraStyle = '';
                    if (data.ftOppositeMatch) {
                        extraStyle = ' style="background-color:#fff9c4;padding:0.125rem 0.25rem;border-radius:0.25rem;"';
                    }
                    const ariaAttr = isGatedRow
                        ? ' aria-label="Locked — requires source access"'
                        : (isIconOnlyDamage && label
                            ? ' aria-label="' + escape(label) + '" title="' + escape(label) + '"'
                            : '');
                    return '<div class="' + rowClasses.join(' ') + '"' + ariaAttr + extraStyle + '>' + icon + labelSpan + ancestryPreviewBadge(data, label) + '</div>';
                },
                item: function(data: any, escape: any) {
                    const label = fateTideDecodeTomSelectLabel(data.text || '');
                    const gatedFlag = ftTomSelectTruth(data.gated);
                    const grandfatheredFlag = ftTomSelectTruth(data.grandfathered);
                    const isGatedRow = sourceGating && gatedFlag && !grandfatheredFlag;
                    const isGrandfathered = sourceGating && grandfatheredFlag;
                    let icon = '';
                    if (isGatedRow) {
                        icon = '<span class="ts-gate-lock-icon text-warning" aria-hidden="true" title="Locked">&#128274;</span>';
                    } else if (showIcons) {
                        icon = ftHydrateOptionIconHtml(data.icon || '');
                    }
                    const rowClasses = ['d-flex', 'align-items-center', 'justify-content-start', 'w-100'];
                    if (isGatedRow) rowClasses.push('ts-option-gated');
                    if (isGrandfathered) rowClasses.push('ts-option-grandfathered');
                    const labelSpan = isIconOnlyDamage ? '' : (matchDropdownToControl
                        ? '<span class="ts-match-control-label" style="margin-left:0.75rem;">' + escape(label) + '</span>'
                        : '<span class="ms-2">' + escape(label) + '</span>');
                    let extraStyle = '';
                    if (data.ftOppositeMatch) {
                        extraStyle = ' style="background-color:#fff9c4;padding:0.125rem 0.25rem;border-radius:0.25rem;"';
                    }
                    const ariaAttr = isIconOnlyDamage && label
                        ? ' aria-label="' + escape(label) + '" title="' + escape(label) + '"'
                        : '';
                    return '<div class="' + rowClasses.join(' ') + '"' + ariaAttr + extraStyle + '>' + icon + labelSpan + ancestryPreviewBadge(data, label) + '</div>';
                }
            },
            escapeMarkup: function(m: any) { return m; },
            maxOptions: 10000,
            // SEARCH THE ITEM TYPE TOO, not just the visible label. Typing a type name used to
            // return only the items coincidentally NAMED after it: "digsite" surfaced just
            // "Ancient Digsite" and hid the other five rungs (Disturbed Earth, Shallow Cache,
            // Old Burial Mound, Collapsed Cellar, Primeval Vault), so a GM had no way to find
            // them in the picker. Types are snake_case; sifter tokenizes the query on
            // whitespace, so "adventuring gear" still matches adventuring_gear. The weights are
            // ranking only (a match on EITHER field still passes the filter) — label hits stay
            // far above type hits, so a real name match is never buried by its type-mates.
            // item_type rides on options loaded from get_options_for_type(); DOM-built static
            // options simply don't carry the field and are unaffected.
            searchField: [{ field: 'text', weight: 3 }, { field: 'item_type', weight: 0.5 }],
            closeAfterSelect: !isMultiple,
            placeholder: placeholder
        };

        // Generic opt-in, for hosts that aren't worth their own closest() rule below: any select can ask for a
        // body-mounted menu with data-ts-dropdown-parent="body". Used by JS-built floating cards (e.g. the
        // sanguine dedicated-cast dialog) whose position:fixed + overflow:auto shell would clip an
        // in-wrapper dropdown. .ts-dropdown already carries z-index 20010, above those cards.
        if (sel.dataset.tsDropdownParent === 'body') {
            tsConfig.dropdownParent = 'body';
        }

        // Feat dropdown: mount menu on body so card overflow/transform/stacking cannot paint a "ghost" second control on hover
        if (!tsConfig.dropdownParent && isFeatTomSelect) {
            tsConfig.dropdownParent = 'body';
        }

        if (!tsConfig.dropdownParent && isInSideInfoPanel) {
            tsConfig.dropdownParent = 'body';
        }

        // Ability Limitations: mount menu on body so it isn’t clipped by nearby sections / stacking contexts
        if (!tsConfig.dropdownParent && isInAbilityLimitations) {
            tsConfig.dropdownParent = 'body';
        }

        if (!tsConfig.dropdownParent && isInCreatePlayerCharacterForm) {
            tsConfig.dropdownParent = 'body';
        }

        if (!tsConfig.dropdownParent && isInModifyEncounterInventoryTab) {
            tsConfig.dropdownParent = 'body';
        }

        if (!tsConfig.dropdownParent && isInModifyEncounterLairsTab) {
            tsConfig.dropdownParent = 'body';
        }

        if (!tsConfig.dropdownParent && isInInventoryLayerRows) {
            tsConfig.dropdownParent = 'body';
        }

        // Encounter random generators (modify encounter tab + GM viewer): compact host; body-mounted menu avoids
        // min-height hacks and scroll-chasing inside div_refresh_gm_viewer / turn widget.
        if (!tsConfig.dropdownParent && sel.closest('#div_encounter_random_generators')) {
            tsConfig.dropdownParent = 'body';
        }

        // Bottom-dock controls retain their explicit upward direction in the shared viewport placement.
        if (sel.dataset.tsDropdownDirection === 'up') tsConfig.dropdownParent = 'body';

        // === LEAFLET POPUP FIX: Prevent clicks/mousedown in dropdown from closing the popup ===
        if (isInPopup) {
            tsConfig.onDropdownOpen = function(this: any) {
                const stopProp = (e: any) => e.stopPropagation();
                this.dropdown.addEventListener('mousedown', stopProp);
                this.dropdown.addEventListener('click', stopProp);
                // Store handler for cleanup
                this._leafletStopProp = stopProp;
            };
            tsConfig.onDropdownClose = function(this: any) {
                if (this._leafletStopProp) {
                    this.dropdown.removeEventListener('mousedown', this._leafletStopProp);
                    this.dropdown.removeEventListener('click', this._leafletStopProp);
                    delete this._leafletStopProp;
                }
            };
        }

        // GENERIC single-select UX (applies to EVERY non-multiple Tom Select — NOT tied to the user_choice
        // feature; the old `select_user_choice` gate was a misnomer for what is really base single-select
        // behaviour). On dropdown OPEN, clear the current pick so the user sees the full list to re-pick;
        // on CLOSE, restore it if they didn't choose anything. This also sidesteps a base-Tom-Select wart
        // where reopening a populated single select leaves the value stuck / renders a duplicate row when
        // options get rebuilt on open (e.g. removeSelectUserChoices on user-choice layers). Restore is
        // robust: if the saved option was dropped from the pool while open (e.g. a dynamic select reloaded
        // its list), re-add it from the snapshot before restoring, so the value is never lost.
        if (!isMultiple) {
            const prevClearOpen = tsConfig.onDropdownOpen;
            tsConfig.onDropdownOpen = function (this: any) {
                this._savedValue = this.getValue();
                this._savedOption = (this._savedValue && this.options) ? this.options[this._savedValue] : null;
                if (this._savedValue) {
                    this.clear(true);
                }
                if (typeof prevClearOpen === 'function') {
                    prevClearOpen.call(this);
                }
            };
            const prevClearClose = tsConfig.onDropdownClose;
            tsConfig.onDropdownClose = function (this: any) {
                if (typeof prevClearClose === 'function') {
                    prevClearClose.call(this);
                }
                // User pressed Backspace/Delete on the (already-emptied) control to clear the field:
                // honor the empty value instead of restoring, and notify dependents (the open dropdown
                // cleared the value silently, so no change fired to reach them).
                if (this._ftUserCleared) {
                    this._ftUserCleared = false;
                    this._savedValue = null;
                    this._savedOption = null;
                    if (sel.id && typeof ftRefreshDynamicFilteredSelects === 'function') {
                        ftRefreshDynamicFilteredSelects(sel);
                    }
                    if (sel.dataset.formQueryItemInfo === 'true' && typeof openItemInfoValue === 'function' && sel.id) {
                        openItemInfoValue(sel.id);
                    }
                    return;
                }
                if (!this.getValue() && this._savedValue) {
                    if (!this.options[this._savedValue] && this._savedOption) {
                        this.addOption(this._savedOption);
                    }
                    if (this.options[this._savedValue]) {
                        this.setValue(this._savedValue, true);
                    }
                }
            };
        }

        // Character creator: user_choice selects use Tom Select; native onclick does not run on the wrapper UI
        const userChoiceLayer = sel.dataset.userChoiceLayer;
        if (userChoiceLayer) {
            const prevOpen = tsConfig.onDropdownOpen;
            tsConfig.onDropdownOpen = function (this: any) {
                if (typeof prevOpen === 'function') {
                    prevOpen.call(this);
                }
                if (typeof removeSelectUserChoices === 'function') {
                    removeSelectUserChoices(userChoiceLayer);
                }
            };
        }
        if (sel.classList.contains('essence_select')) {
            const prevEssenceOpen = tsConfig.onDropdownOpen;
            tsConfig.onDropdownOpen = function (this: any) {
                if (typeof prevEssenceOpen === 'function') {
                    prevEssenceOpen.call(this);
                }
                if (typeof calcEssences === 'function') {
                    calcEssences();
                }
            };
        }

        // Feat selects: on first dropdown open, fetch character-filtered feat options
        const featSourceId = sel.dataset.featSourceId;
        if (sel.classList.contains('feat') && featSourceId) {
            const prevFeatOpen = tsConfig.onDropdownOpen;
            tsConfig.onDropdownOpen = function (this: any) {
                if (typeof prevFeatOpen === 'function') {
                    prevFeatOpen.call(this);
                }
                if (!this._featFiltered && typeof getSelectFeat === 'function') {
                    this._featFiltered = true;
                    getSelectFeat(featSourceId);
                }
                if (typeof removeSelectUserChoices === 'function') {
                    removeSelectUserChoices('feat');
                }
            };
        }

        if (matchDropdownToControl) {
            const pinDropdownToControlWidth = function (this: any) {
                if (!this.dropdown || !this.control) return;
                const w = Math.round(this.control.getBoundingClientRect().width);
                if (w < 1) return;
                this.dropdown.style.setProperty('width', w + 'px', 'important');
                this.dropdown.style.setProperty('max-width', w + 'px', 'important');
                this.dropdown.style.setProperty('box-sizing', 'border-box', 'important');
            };
            const prevPinOpen = tsConfig.onDropdownOpen;
            tsConfig.onDropdownOpen = function (this: any) {
                if (typeof prevPinOpen === 'function') {
                    prevPinOpen.call(this);
                }
                const self = this;
                queueMicrotask(function () { pinDropdownToControlWidth.call(self); });
                requestAnimationFrame(function () { pinDropdownToControlWidth.call(self); });
            };
            const prevPinClose = tsConfig.onDropdownClose;
            tsConfig.onDropdownClose = function (this: any) {
                if (this.dropdown) {
                    this.dropdown.style.removeProperty('width');
                    this.dropdown.style.removeProperty('max-width');
                    this.dropdown.style.removeProperty('box-sizing');
                }
                if (typeof prevPinClose === 'function') {
                    prevPinClose.call(this);
                }
            };
        }

        if (isInSideInfoPanel) {
            const prevSideInfoZ = tsConfig.onDropdownOpen;
            tsConfig.onDropdownOpen = function (this: any) {
                if (typeof prevSideInfoZ === 'function') {
                    prevSideInfoZ.call(this);
                }
                if (this.dropdown) {
                    this.dropdown.style.zIndex = '20100';
                }
            };
        }

        const ts = new TomSelect(sel, tsConfig);

        if (tsConfig.dropdownParent === 'body') {
            installBodyDropdownPlacement(ts, sel.dataset.tsDropdownDirection === 'up');
        }

        // Single-select: let Backspace/Delete on an empty search input clear the field to empty.
        // On dropdown open the value is stashed + cleared silently (see onDropdownOpen above), so there is
        // no chip for Tom Select's native backspace-delete to grab and no way to reach empty — this flags the
        // intent so onDropdownClose skips the restore. (Mouse users get the '×' clear_button; this is the
        // keyboard equivalent.) closeAfterSelect single-selects already close on pick, so this only affects clears.
        if (!isMultiple && ts.control_input) {
            ts.control_input.addEventListener('keydown', function (e: any) {
                if ((e.key === 'Backspace' || e.key === 'Delete') && !ts.control_input.value) {
                    ts._ftUserCleared = true;
                    ts.close();
                }
            });
        }

        if (sel.dataset.ftOnlyOwnedCheckbox) {
            ftWireDynamicSelectOnlyOwnedCheckbox(sel, ts);
        }
        if (sourceGating) {
            ftTomSelectSyncSourceGatingFromNativeSelectHtml(sel, ts);
        }

        // ------------------------------------------------------------------
        // PRE-SELECTED: Ensure icons respect data-show-icons immediately
        // ------------------------------------------------------------------
        let tomSelectLabelOrIconFixed = false;
        Array.from(sel.options).filter((opt: any) =>
            opt.selected && opt.value !== '' && opt.value !== null
        ).forEach((opt: any) => {
            const value = opt.value;
            const existing = ts.options[value];

            // Icon-only table options deliberately carry no text. Their title is the authored
            // searchable/accessibility label; never replace that empty presentation with "Unknown".
            const iconOnlyLabel = isIconOnlyDamage ? String(opt.getAttribute('title') || '').trim() : '';
            const optionText = fateTideDecodeTomSelectLabel(opt.textContent.trim() || iconOnlyLabel || 'Unknown');

            if (!existing) {
                const preOpt: any = {
                    value: value,
                    text: optionText,
                    icon: showIcons ? (opt.dataset.icon || '') : '',
                };
                if (sourceGating) {
                    if (opt.dataset.gated === '1') {
                        preOpt.gated = true;
                    } else if (opt.dataset.gated === '0') {
                        preOpt.gated = false;
                    }
                    if (opt.dataset.grandfathered === '1') {
                        preOpt.grandfathered = true;
                    } else if (opt.dataset.grandfathered === '0') {
                        preOpt.grandfathered = false;
                    }
                    const gu = opt.dataset.gateUrl || '';
                    if (gu) {
                        preOpt.gate_url = gu;
                    }
                }
                ts.addOption(preOpt);
            } else {
                let changed = false;
                if (existing.text !== optionText) {
                    existing.text = optionText;
                    changed = true;
                }
                if (showIcons && opt.dataset.icon && !existing.icon) {
                    existing.icon = opt.dataset.icon;
                    changed = true;
                } else if (!showIcons && existing.icon) {
                    existing.icon = '';
                    changed = true;
                }
                if (sourceGating) {
                    if (opt.dataset.gated === '1') {
                        existing.gated = true;
                        changed = true;
                    } else if (opt.dataset.gated === '0') {
                        existing.gated = false;
                        changed = true;
                    }
                    if (opt.dataset.grandfathered === '1') {
                        existing.grandfathered = true;
                        changed = true;
                    } else if (opt.dataset.grandfathered === '0') {
                        existing.grandfathered = false;
                        changed = true;
                    }
                    const gu = opt.dataset.gateUrl || '';
                    if (gu && existing.gate_url !== gu) {
                        existing.gate_url = gu;
                        changed = true;
                    }
                }
                if (changed) {
                    ts.refreshOptions(false);
                    tomSelectLabelOrIconFixed = true;
                }
            }

            if (!ts.items.includes(value)) {
                ts.addItem(value, true);
            }
        });
        if (tomSelectLabelOrIconFixed && typeof ts.refreshItems === 'function') {
            ts.refreshItems();
        }

        if (sel.id && document.querySelector('select.dynamic-item-select[data-ft-dynamic-filter-by="' + ((typeof CSS !== 'undefined' && typeof CSS.escape === 'function') ? CSS.escape(sel.id) : sel.id.replace(/(["\\])/g, '\\$1')) + '"]')) {
            const preFilterValue = (ts.getValue() || '').trim();
            if (preFilterValue) {
                queueMicrotask(function () {
                    ftRefreshDynamicFilteredSelects(sel);
                });
            }
        }

        // Static <select>: Tom Select often omits data-icon on non-selected options. Copy it from
        // native, and give icon-only rows their authored title as hidden searchable/ARIA text.
        if (!isDynamic && showIcons) {
            let staticOptionsPatched = false;
            Array.from(sel.options).forEach(function (opt: any) {
                if (!opt.value) return;
                const ex = ts.options[opt.value];
                if (ex && opt.dataset.icon && ex.icon !== opt.dataset.icon) {
                    ex.icon = opt.dataset.icon;
                    staticOptionsPatched = true;
                }
                if (ex && isIconOnlyDamage) {
                    const label = fateTideDecodeTomSelectLabel(String(opt.getAttribute('title') || '').trim());
                    if (label && ex.text !== label) {
                        ex.text = label;
                        staticOptionsPatched = true;
                    }
                }
            });
            if (staticOptionsPatched) {
                ts.refreshOptions(false);
                ts.refreshItems();
            }
        }

        // Encounter inventory links: opposite-door / opposite-map-marker row highlight (data-ft-opposite-match on native <option>)
        if (sel.id === 'inventory_encounter_links') {
            Array.from(sel.options).forEach(function (opt: any) {
                if (!opt.value) {
                    return;
                }
                const ex = ts.options[opt.value];
                if (!ex) {
                    return;
                }
                if (opt.getAttribute('data-ft-opposite-match') === '1') {
                    ex.ftOppositeMatch = true;
                } else {
                    delete ex.ftOppositeMatch;
                }
            });
            ts.refreshOptions(false);
            if (typeof ts.refreshItems === 'function') {
                ts.refreshItems();
            }
        }

        // Character creator: PHP may emit data-user-choice-selected when native selected="" mismatches (UUID case) or TS missed sync
        const forcedSaved = (sel.dataset.userChoiceSelected || '').trim();
        if (forcedSaved !== '' && sel.classList.contains('select_user_choice')) {
            const wanted = forcedSaved.toLowerCase();
            const matchOpt = Array.from(sel.options).find((o: any) => o.value && o.value.toLowerCase() === wanted) as any;
            if (matchOpt) {
                const v = matchOpt.value;
                const optionText = fateTideDecodeTomSelectLabel(matchOpt.textContent.trim() || 'Unknown');
                if (!ts.options[v]) {
                    ts.addOption({
                        value: v,
                        text: optionText,
                        icon: showIcons ? (matchOpt.dataset.icon || '') : ''
                    });
                } else if (ts.options[v].text !== optionText) {
                    ts.options[v].text = optionText;
                    ts.refreshOptions(false);
                }
                if (!ts.items.includes(v)) {
                    ts.addItem(v, true);
                }
            }
        }

        // Initial width fix on load (in case something is pre-selected)
        if (isFeatTomSelect || isFullWidth || isInSideInfoPanel || isInAbilityLimitations || isInEncounterInvAddRow || isInInventoryLayerRows) {
            ts.wrapper.style.width = '100%';
            ts.control.style.width = '100%';
        } else {
            ts.wrapper.style.width = 'fit-content';
            ts.control.style.width = 'fit-content';
        }

        // Initial refresh if dropdown already open (rare but safe)
        if (ts.isOpen) {
            ts.refreshOptions(false);
        }
    });
}

/**
 * Re-sync Tom Select UI for character creator user_choice dropdowns (bootstrap-select-style "refresh").
 * Call after initializeTomSelect / AJAX injects when native <option selected> or data-user-choice-selected must show.
 */
function syncUserChoiceTomSelectsFromNative(root: ParentNode = document): void {
    root.querySelectorAll('select.select_user_choice.ts-select.tomselected').forEach((sel: any) => {
        const ts = sel.tomselect;
        if (!ts) return;

        const forced = (sel.dataset.userChoiceSelected || '').trim();
        if (forced !== '') {
            const wanted = forced.toLowerCase();
            const matchOpt = Array.from(sel.options).find((o: any) => o.value && o.value.toLowerCase() === wanted) as any;
            if (matchOpt) {
                const v = matchOpt.value;
                const optionText = fateTideDecodeTomSelectLabel(matchOpt.textContent.trim() || 'Unknown');
                const showIcons = sel.dataset.showIcons !== 'false';
                if (!ts.options[v]) {
                    ts.addOption({
                        value: v,
                        text: optionText,
                        icon: showIcons ? (matchOpt.dataset.icon || '') : ''
                    });
                } else if (ts.options[v].text !== optionText) {
                    ts.options[v].text = optionText;
                    ts.refreshOptions(false);
                }
                if (ts.items.length === 0) {
                    ts.addItem(v, true);
                }
            }
            return;
        }

        const selected = Array.from(sel.options).find((o: any) => o.selected && o.value) as any;
        if (!selected) return;
        if (!ts.items.includes(selected.value)) {
            const optionText = fateTideDecodeTomSelectLabel(selected.textContent.trim() || 'Unknown');
            const showIcons = sel.dataset.showIcons !== 'false';
            if (!ts.options[selected.value]) {
                ts.addOption({
                    value: selected.value,
                    text: optionText,
                    icon: showIcons ? (selected.dataset.icon || '') : ''
                });
            } else if (ts.options[selected.value].text !== optionText) {
                ts.options[selected.value].text = optionText;
                ts.refreshOptions(false);
            }
            ts.addItem(selected.value, true);
        }
    });
}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Tom Select dynamic-item selects lazy-load options on focus; setValue(uuid) is ignored until the option exists.
 * Fetches the type list (same endpoint as the control), adds the chosen option, then sets the value.
 * @param {{ silent?: boolean, base?: string, addOnly?: boolean }} [options]
 *        addOnly — register the option but do not change the current selection (for multi-select prefetch).
 */
async function ensureDynamicTomSelectOption(selectIdOrEl: any, itemId: any, options: any = {}): Promise<void> {
	const silent = options.silent ?? true;
	const addOnly = !!options.addOnly;
	const rawBase = options.base || (typeof BASE_URL !== 'undefined' ? BASE_URL : '/');
	const base = rawBase.endsWith('/') ? rawBase : rawBase + '/';
	const el: any = typeof selectIdOrEl === 'string' ? document.getElementById(selectIdOrEl) : selectIdOrEl;
	if (!el) {
		return;
	}
	const selectionRequest = addOnly ? null : Symbol('dynamic-selection');
	if (!addOnly) el._ftDynamicSelectionRequest = selectionRequest;
	const v = itemId != null && itemId !== '' ? String(itemId).trim() : '';
	if (!v) {
		if (!addOnly) {
			setTomSelectValue(el, '', { silent });
		}
		return;
	}
	const ts = el.tomselect;
	if (!ts) {
		if (!addOnly) {
			setTomSelectValue(el, v, { silent });
		}
		return;
	}
	const selectionBefore = JSON.stringify(ts.getValue());
	const vLower = v.toLowerCase();
	const showIcons = el.dataset.showIcons !== 'false';
	const thumbnailSource = el.dataset.thumbnailSource === 'item_picture' ? 'item_picture' : 'icon';
	const keyFound = ts.options[v] ? v : Object.keys(ts.options).find((k: any) => k.toLowerCase() === vLower);
	if (keyFound && (thumbnailSource !== 'item_picture' || !showIcons)) {
		if (!addOnly) {
			ts.setValue(keyFound, silent);
		}
		return;
	}
	const type = el.dataset.itemType;
	if (!type) {
		ts.addOption({ value: v, text: 'Unknown', icon: '' });
		if (!addOnly) {
			ts.setValue(v, silent);
		}
		return;
	}
	const homebrew = el.dataset.homebrew !== 'false';
	const onlyOwned = el._ftOnlyOwnedSearch === true;
	const requireExplicitSourceEntitlement = el.dataset.requireExplicitSourceEntitlement === 'true';
	const filterItemType = ftDynamicFilterValueForSelect(el);
	const filterSeg = filterItemType ? '_' + filterItemType : '';
	const key = `${type}_${homebrew ? '1' : '0'}_${thumbnailSource}${filterSeg}_${onlyOwned ? 'own1' : 'own0'}_${requireExplicitSourceEntitlement ? 'claim1' : 'claim0'}${ftDynamicSelectCampaignKeySeg()}`;
	const campaignIdForEnsure = ftDynamicSelectCampaignId();
	const typePayload: any = { type, homebrew, thumbnail_source: thumbnailSource, preselected: [],
		only_owned: onlyOwned, require_explicit_source_entitlement: requireExplicitSourceEntitlement };
	if (filterItemType) typePayload.filter_item_type = filterItemType;
	if (campaignIdForEnsure) typePayload.campaign_id = campaignIdForEnsure;
	let rawOpts: any[];
	try {
		rawOpts = await ftDynamicSelectLists.load(base + 'options/tools/items/get_items_for_dynamic_select.php', key, typePayload);
		// Saved/native options need the same art as normal list loading. Hydrate only
		// the requested identity here; the neutral list cache retains the result.
		if (thumbnailSource === 'item_picture' && showIcons) {
			await hydrateDynamicOptionPictures(rawOpts.filter(o => String(o.value).toLowerCase() === vLower), base);
		}
	} catch (error) {
		console.error('ensureDynamicTomSelectOption fetch failed', error);
		return;
	}
	// A replaced/retargeted control must not adopt a result from its previous scope.
	if (el.tomselect !== ts || el.dataset.itemType !== type
		|| (!addOnly && el._ftDynamicSelectionRequest !== selectionRequest)
		|| (!addOnly && JSON.stringify(ts.getValue()) !== selectionBefore)
		|| (el.dataset.homebrew !== 'false') !== homebrew
		|| (el.dataset.thumbnailSource === 'item_picture' ? 'item_picture' : 'icon') !== thumbnailSource
		|| (el.dataset.showIcons !== 'false') !== showIcons
		|| ftDynamicFilterValueForSelect(el) !== filterItemType
		|| (el._ftOnlyOwnedSearch === true) !== onlyOwned
		|| (el.dataset.requireExplicitSourceEntitlement === 'true') !== requireExplicitSourceEntitlement
		|| ftDynamicSelectCampaignId() !== campaignIdForEnsure) return;

	const sourceGating = el.dataset.sourceGating === 'true';
	const mapped = ftApplyPreselectedGrandfather(rawOpts || [], [v]).map((o: any) => {
		const gated = !!o.gated;
		const grandfathered = !!o.grandfathered;
		const gateUrl = o.gate_url || '';
		let iconHtml = '';
		if (sourceGating && gated && !grandfathered) {
			iconHtml = '<span class="ts-gate-lock-icon text-warning" aria-hidden="true" title="Locked">&#128274;</span>';
		} else if (showIcons && (o.icon_url || o.icon_key || o.item_type)) {
			iconHtml = ftAjaxOptionIcon(o);
		}
		return {
			value: o.value || o.item_id || o.id || '',
			text: fateTideDecodeTomSelectLabel(o.label || o.item_name || o.name || 'Unknown'),
			item_type: String(o.item_type || ''), // searchable — see the searchField note
			icon: iconHtml,
			gated,
			grandfathered,
			gate_url: gateUrl,
		};
	});
	let pick: any = mapped.find((o: any) => o.value === v || (o.value && o.value.toLowerCase() === vLower));
	if (!pick) {
		pick = keyFound ? ts.options[keyFound] : { value: v, text: 'Unknown', icon: '' };
	}
	if (keyFound) pick = { ...pick, value: keyFound };
	if (!ts.options[pick.value]) {
		ts.addOption(pick);
	} else {
		ts.updateOption(pick.value, { ...ts.options[pick.value], ...pick });
	}
	if (!addOnly) {
		ts.setValue(pick.value, silent);
	}
}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function setTomSelectValue(selectIdOrEl: any, value: any, options: any = {}): void {
    const el: any = typeof selectIdOrEl === 'string' ? document.getElementById(selectIdOrEl) : selectIdOrEl;
    if (!el) return;

    const silent = options.silent ?? false;
    const isEmpty =
        value === '' ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0);

    // Tom Select must be updated before any change listeners run: getElementValue() reads ts.getValue(),
    // not el.value (see random table "clear all attached" which used silent saves that still fired change first).
    if (el.tomselect) {
        const ts = el.tomselect;
        if (isEmpty && typeof ts.clear === 'function') {
            ts.clear(silent);
        } else {
            ts.setValue(isEmpty ? '' : value, silent);
        }
    } else {
        el.value = isEmpty ? '' : value;
        if (!silent) {
            el.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    if (!silent) {
        el.dispatchEvent(new Event('change', { bubbles: true }));
    }
}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// ===========================================================================================
// Window attachment — preserve the EXACT public surface of the original plain-JS tom-select-init.js
// (all 12 top-level functions became window globals when it loaded as a plain <script>). 6 are
// grep-confirmed cross-file consumed (initializeTomSelect / destroyTomSelectIn / setTomSelectValue /
// ensureDynamicTomSelectOption / syncUserChoiceTomSelectsFromNative / fateTideDecodeTomSelectLabel);
// the other 6 are internal today but kept on window to mirror the original surface 1:1 (PHP inline
// onclick handlers can reference any by bare name).
// ===========================================================================================
declare global {
    interface Window {
        _ftTomSelectPointerCaptureInstalled?: boolean;
        _ftTomSelectLastPointerDown?: any;
        ftUpdateInventoryDescriptionScriptPreview?: (form: any) => void;
        destroyTomSelectIn: typeof destroyTomSelectIn;
        fateTideDecodeTomSelectLabel: typeof fateTideDecodeTomSelectLabel;
        ftTomSelectTruth: typeof ftTomSelectTruth;
        ftTomSelectSyncSourceGatingFromNativeSelectHtml: typeof ftTomSelectSyncSourceGatingFromNativeSelectHtml;
        ftWireDynamicSelectOnlyOwnedCheckbox: typeof ftWireDynamicSelectOnlyOwnedCheckbox;
        fateTideInstallTomSelectPointerCapture: typeof fateTideInstallTomSelectPointerCapture;
        ftResolveSelectIconKeys: typeof ftResolveSelectIconKeys;
        ftAjaxOptionIcon: typeof ftAjaxOptionIcon;
        ftClearDynamicSelectOptionsCache: typeof ftClearDynamicSelectOptionsCache;
        initializeTomSelect: typeof initializeTomSelect;
        syncUserChoiceTomSelectsFromNative: typeof syncUserChoiceTomSelectsFromNative;
        ensureDynamicTomSelectOption: typeof ensureDynamicTomSelectOption;
        setTomSelectValue: typeof setTomSelectValue;
    }
}

window.destroyTomSelectIn = destroyTomSelectIn;
window.fateTideDecodeTomSelectLabel = fateTideDecodeTomSelectLabel;
window.ftTomSelectTruth = ftTomSelectTruth;
window.ftTomSelectSyncSourceGatingFromNativeSelectHtml = ftTomSelectSyncSourceGatingFromNativeSelectHtml;
window.ftWireDynamicSelectOnlyOwnedCheckbox = ftWireDynamicSelectOnlyOwnedCheckbox;
window.fateTideInstallTomSelectPointerCapture = fateTideInstallTomSelectPointerCapture;
window.ftResolveSelectIconKeys = ftResolveSelectIconKeys;
window.ftAjaxOptionIcon = ftAjaxOptionIcon;
window.ftClearDynamicSelectOptionsCache = ftClearDynamicSelectOptionsCache;
window.initializeTomSelect = initializeTomSelect;
window.syncUserChoiceTomSelectsFromNative = syncUserChoiceTomSelectsFromNative;
window.ensureDynamicTomSelectOption = ensureDynamicTomSelectOption;
window.setTomSelectValue = setTomSelectValue;

export {};
